import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  useMissions,
  useMissionStats,
  useUpdateMission,
  useDeleteMission,
  getDifficultyColor,
  getStatusColor,
  getStatusLabel,
} from "@/hooks/useMissions";
import type { Mission, MissionStatus } from "@/hooks/useMissions";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  HiOutlineCheck,
  HiOutlineTrash,
  HiOutlinePlay,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from "react-icons/hi";
import { Flag, Compass, Map, Settings, Target, Users, CheckCircle2 } from "lucide-react";

function CategoryIcon({ category }: { category: string }) {
  const className = "h-6 w-6 text-violet-600";
  switch (category) {
    case "setup":
      return <Settings className={className} />;
    case "practice":
      return <Target className={className} />;
    case "share":
      return <Users className={className} />;
    case "verify":
      return <CheckCircle2 className={className} />;
    default:
      return <Target className={className} />;
  }
}

export const Route = createFileRoute("/_auth/missions")({
  component: MissionsPage,
});

function MissionsPage() {
  const [statusFilter, setStatusFilter] = useState<MissionStatus | undefined>(
    undefined
  );
  const { data: missions, isLoading, isError } = useMissions(statusFilter);
  const { data: stats } = useMissionStats();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24">
      {/* Nav header */}
      <div className="sticky top-0 z-20 border-b border-white/60 bg-white/70 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Link
              to="/learn"
              search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
              className="text-slate-500 hover:text-slate-700 transition-colors"
            >
              Training
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-slate-700 font-medium">Missions</span>
          </div>
          <Link
            to="/learn"
            search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
            className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
          >
            Browse Training
          </Link>
        </div>
      </div>

      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 pt-10">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-2">
            My Missions
          </h1>
          <p className="text-lg text-slate-600">
            Real-world actions to strengthen your security.
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mb-8 rounded-3xl border border-white/70 bg-white/70 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-200/80 bg-white/70 p-4 text-center">
                <p className="text-2xl font-semibold text-slate-900">{stats.pending}</p>
                <p className="text-xs text-slate-500">To Do</p>
              </div>
              <div className="rounded-2xl border border-slate-200/80 bg-white/70 p-4 text-center">
                <p className="text-2xl font-semibold text-slate-900">{stats.inProgress}</p>
                <p className="text-xs text-slate-500">In Progress</p>
              </div>
              <div className="rounded-2xl border border-slate-200/80 bg-white/70 p-4 text-center">
                <p className="text-2xl font-semibold text-slate-900">{stats.completed}</p>
                <p className="text-xs text-slate-500">Done</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="sticky top-12 bg-white/70 border-b border-white/60 backdrop-blur-xl px-4 py-3 z-10">
        <div className="max-w-3xl mx-auto flex gap-2 overflow-x-auto">
          <FilterButton
            active={statusFilter === undefined}
            onClick={() => setStatusFilter(undefined)}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === "PENDING"}
            onClick={() => setStatusFilter("PENDING")}
          >
            To Do
          </FilterButton>
          <FilterButton
            active={statusFilter === "IN_PROGRESS"}
            onClick={() => setStatusFilter("IN_PROGRESS")}
          >
            In Progress
          </FilterButton>
          <FilterButton
            active={statusFilter === "COMPLETED"}
            onClick={() => setStatusFilter("COMPLETED")}
          >
            Done
          </FilterButton>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton height={140} borderRadius={24} />
            <Skeleton height={140} borderRadius={24} />
            <Skeleton height={140} borderRadius={24} />
          </div>
        ) : isError ? (
          <div className="text-center py-12">
            <p className="text-red-500">Failed to load missions</p>
          </div>
        ) : missions && missions.length > 0 ? (
          <div className="space-y-4">
            {missions.map((mission) => (
              <MissionCard key={mission.id} mission={mission} />
            ))}
          </div>
        ) : (
          <EmptyState statusFilter={statusFilter} />
        )}
      </div>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white/70 text-slate-600 border-slate-200/80 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function MissionCard({ mission }: { mission: Mission }) {
  const [expanded, setExpanded] = useState(false);
  const updateMission = useUpdateMission();
  const deleteMission = useDeleteMission();

  const steps = Array.isArray(mission.steps) ? mission.steps : [];
  const stepsCompleted = Array.isArray(mission.stepsCompleted)
    ? mission.stepsCompleted
    : [];

  const handleStart = async () => {
    await updateMission.mutateAsync({
      missionId: mission.id,
      status: "IN_PROGRESS",
    });
  };

  const handleComplete = async () => {
    await updateMission.mutateAsync({
      missionId: mission.id,
      status: "COMPLETED",
    });
  };

  const handleToggleStep = async (stepId: string) => {
    const newStepsCompleted = stepsCompleted.includes(stepId)
      ? stepsCompleted.filter((id) => id !== stepId)
      : [...stepsCompleted, stepId];

    await updateMission.mutateAsync({
      missionId: mission.id,
      stepsCompleted: newStepsCompleted,
    });
  };

  const handleDelete = async () => {
    if (confirm("Remove this mission from your list?")) {
      await deleteMission.mutateAsync(mission.id);
    }
  };

  const isCompleted = mission.status === "COMPLETED";

  return (
    <div
      className={`rounded-3xl border bg-white/70 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] overflow-hidden ${
        isCompleted ? "border-emerald-200/70" : "border-white/70"
      }`}
    >
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <span className="mt-0.5"><CategoryIcon category={mission.category} /></span>
            <div className="flex-1 min-w-0">
              <h3
                className={`font-semibold ${
                  isCompleted ? "text-emerald-800" : "text-slate-900"
                }`}
              >
                {mission.title}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(
                    mission.status
                  )}`}
                >
                  {getStatusLabel(mission.status)}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${getDifficultyColor(
                    mission.difficulty
                  )}`}
                >
                  {mission.difficulty}
                </span>
                {mission.estimatedTime && (
                  <span className="text-xs text-slate-500">
                    {mission.estimatedTime}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-slate-100/70 rounded-xl transition-colors"
          >
            {expanded ? (
              <HiOutlineChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <HiOutlineChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
        </div>

        {/* Progress bar for steps */}
        {steps.length > 0 && !isCompleted && (
          <div className="mt-3">
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{
                  width: `${(stepsCompleted.length / steps.length) * 100}%`,
                }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {stepsCompleted.length} of {steps.length} steps
            </p>
          </div>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100/70 pt-5">
          {mission.description && (
            <p className="text-slate-600 text-sm mb-4">{mission.description}</p>
          )}

          {mission.why && (
            <div className="bg-blue-50/70 rounded-2xl p-4 mb-4 border border-blue-100">
              <p className="text-xs text-blue-600 font-medium mb-1">
                Why This Matters
              </p>
              <p className="text-sm text-blue-800">{mission.why}</p>
            </div>
          )}

          {/* Steps checklist */}
          {steps.length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-sm font-medium text-slate-700">Steps:</p>
              {steps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => !isCompleted && handleToggleStep(step.id)}
                  disabled={isCompleted}
                  className={`w-full flex items-start gap-3 p-2 rounded-xl text-left transition-colors ${
                    isCompleted
                      ? "cursor-default"
                      : "hover:bg-slate-50 cursor-pointer"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      stepsCompleted.includes(step.id) || isCompleted
                        ? "bg-green-500 border-green-500"
                        : "border-slate-300"
                    }`}
                  >
                    {(stepsCompleted.includes(step.id) || isCompleted) && (
                      <HiOutlineCheck className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span
                    className={`text-sm ${
                      stepsCompleted.includes(step.id) || isCompleted
                        ? "text-slate-400 line-through"
                        : "text-slate-700"
                    }`}
                  >
                    {step.text}
                    {step.isOptional && (
                      <span className="text-slate-400 ml-1">(optional)</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}

          {mission.successCriteria && (
            <div className="bg-emerald-50/70 rounded-2xl p-4 mb-4 border border-emerald-100">
              <p className="text-xs text-emerald-600 font-medium mb-1">
                You'll Know You're Done When
              </p>
              <p className="text-sm text-emerald-800">{mission.successCriteria}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            {mission.status === "PENDING" && (
              <button
                onClick={handleStart}
                disabled={updateMission.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                <HiOutlinePlay className="w-4 h-4" />
                Start Mission
              </button>
            )}

            {mission.status === "IN_PROGRESS" && (
              <button
                onClick={handleComplete}
                disabled={updateMission.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <HiOutlineCheck className="w-4 h-4" />
                Mark Complete
              </button>
            )}

            {!isCompleted && (
              <button
                onClick={handleDelete}
                disabled={deleteMission.isPending}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50"
              >
                <HiOutlineTrash className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Reward */}
          {(mission.xpReward > 0 || mission.badgeId) && (
            <div className="flex items-center justify-center gap-4 pt-4 mt-4 border-t border-slate-100/70">
              {mission.xpReward > 0 && (
                <span className="text-sm text-amber-600 font-medium">
                  +{mission.xpReward} GP
                </span>
              )}
              {mission.badgeId && (
                <span className="text-sm text-purple-600 font-medium">
                  {mission.badgeId} badge
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ statusFilter }: { statusFilter?: MissionStatus }) {
  if (statusFilter === "COMPLETED") {
    return (
      <div className="text-center py-12">
        <Flag className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No completed missions yet
        </h3>
        <p className="text-slate-500 mb-6">
          Complete missions to see them here
        </p>
        <Link
          to="/missions"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
        >
          View All Missions
        </Link>
      </div>
    );
  }

  if (statusFilter) {
    return (
      <div className="text-center py-12">
        <Compass className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No missions in this category
        </h3>
        <p className="text-slate-500 mb-6">
          Try a different filter or return to all missions.
        </p>
        <Link
          to="/missions"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
        >
          Show All
        </Link>
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <Map className="mx-auto mb-4 h-10 w-10 text-slate-300" />
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        No missions yet
      </h3>
      <p className="text-slate-500 mb-6">
        Complete training paths to unlock real-world missions
      </p>
      <Link
        to="/learn"
        search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
        className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
      >
        Start Learning
      </Link>
    </div>
  );
}
